import { Component, OnInit } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { environment } from 'src/environments/environment';

@Component({
  selector: 'app-message-ai',
  templateUrl: './message-ai.component.html',
  styleUrls: ['./message-ai.component.css']
})
export class MessageAiComponent implements OnInit {
  // User input
  userQuestion: string = ''; // Free-style question from user
  
  // Processing states
  isLoading: boolean = false;
  mappingInProgress: boolean = false;
  
  // Results
  messageContent: string = ''; // Final binary answer
  mappedQuestion: string = ''; // The mapped predefined question
  
  // Variables for user modification
  userSpecies: string = '';
  userProtein1: string = '';
  userProtein2: string = '';
  extractedEntities: any = null; // Store extracted entities
  
  // Demo mode
  showDemo: boolean = true; // Show demo example by default
  demoQuestion: string = 'Can Calcium-dependent protein kinases such as CPK27 directly interact with and phosphorylated tonoplast sugar transporter 2?';
  demoMappedQuestion: string = "In 'Arabidopsis thaliana', do the kinase 'CPK27' phosphorylate 'tonoplast sugar transporter 2'? Please answer yes or no.";
  demoAnswer: string = "Answer: YES"
  
  // Predefined questions
  predefinedQuestions = [
    "In '{Species}', for protein, '{ProteinName}', can it be phosphorylated? Please answer yes or no.",
    "In '{Species}', do the proteins '{ProteinName1}' and '{ProteinName2}' form a protein-protein interaction (PPI)? Please answer yes or no.",
    "In '{Species}', do the kinase '{ProteinName1}' phosphorylate '{ProteinName2}'? Please answer yes or no.",
    "In '{Species}', what's the potential kinase for protein '{ProteinName}'? Please list them without other sentences.",
    "In '{Species}', what's protein-protein interaction partners for protein '{ProteinName}'? Please list them without other sentences.",
    "The question is outside of our current scope."
  ];
  
  // Display states
  showPredefinedQuestions: boolean = false;
  showResults: boolean = false;

  constructor(private httpClient: HttpClient) {}

  ngOnInit(): void {}

  // Toggle display of predefined questions
  togglePredefinedQuestions(): void {
    this.showPredefinedQuestions = !this.showPredefinedQuestions;
  }

  // Submit the user's free-style question
  onSubmit(): void {
    if (!this.userQuestion.trim()) {
      this.messageContent = 'Please enter a question.';
      return;
    }
    
    // Debug: Check if user is using the demo question
    const isUsingDemoQuestion = this.userQuestion === this.demoQuestion;
    console.log('Is user using the demo question?', isUsingDemoQuestion);
    
    // Reset all previous results and entities
    this.isLoading = true;
    this.mappingInProgress = true;
    this.showResults = true;
    this.messageContent = ''; // Clear previous answer
    this.mappedQuestion = ''; // Clear previous mapped question
    
    // Clear all extracted entities
    this.userSpecies = '';
    this.userProtein1 = '';
    this.userProtein2 = '';
    this.extractedEntities = null;
    
    // First, map the user question to one of the predefined questions
    this.mapToPreDefinedQuestion();
  }
  
  // Map user question to a predefined question using PTM Site API
  mapToPreDefinedQuestion(): void {
    // Define the API URL for processing questions
    const apiUrl = 'https://ptmsite.info:5004/api/process_question/';
    
    // Prepare the request body
    const requestBody = {
      question: this.userQuestion
    };
    
    // Set headers for JSON content
    const headers = { 'Content-Type': 'application/json' };
    
    console.log('Sending question to process API:', this.userQuestion);
    
    // Make the API call to process the question
    this.httpClient.post(apiUrl, requestBody, { headers }).subscribe({
      next: (result: any) => {
        console.log('PTM Site API response:', result);
        
        if (result && result.status === 1 && result.data && Array.isArray(result.data) && result.data.length > 0) {
          const responseData = result.data[0];
          console.log('Processing response data:', responseData);
          
          // Store the formulated question from the API response
          if (responseData.formulated_question) {
            this.mappedQuestion = responseData.formulated_question;
            console.log(`Mapped to question: ${this.mappedQuestion}`);
          } else {
            // If no formulated question is returned, use a more specific message
            this.mappedQuestion = "This question is outside of our current scope. Please try asking about protein phosphorylation, protein-protein interactions, or kinase activity.";
          }
          
          // Extract and store species from the response
          if (responseData.species) {
            this.userSpecies = responseData.species;
            console.log(`Extracted species: ${this.userSpecies}`);
          }
          
          // Extract and store protein names from the response
          if (responseData.proteinNames && Array.isArray(responseData.proteinNames)) {
            if (responseData.proteinNames.length > 0) {
              this.userProtein1 = responseData.proteinNames[0] || '';
              console.log(`Extracted protein 1: ${this.userProtein1}`);
            }
            
            if (responseData.proteinNames.length > 1) {
              this.userProtein2 = responseData.proteinNames[1] || '';
              console.log(`Extracted protein 2: ${this.userProtein2}`);
            } else {
              this.userProtein2 = '';
            }
          }
          
          this.mappingInProgress = false;
          
          // Check if the question is outside of scope
          if (this.mappedQuestion.toLowerCase().includes("outside of our current scope")) {
            this.messageContent = "This question is outside of our current scope. Please try asking about protein phosphorylation, protein-protein interactions, or kinase activity.";
            this.isLoading = false;
          } else {
            // Proceed to get the answer using the formulated question
            this.getAnswerForMappedQuestion(this.mappedQuestion);
          }
        } else {
          // Handle error or unexpected response format
          console.error('Invalid or unexpected API response format:', result);
          this.mappingInProgress = false;
          this.isLoading = false;
          this.mappedQuestion = "This question is outside of our current scope. Please try asking about protein phosphorylation, protein-protein interactions, or kinase activity.";
          this.messageContent = "This question is outside of our current scope. Please try asking about protein phosphorylation, protein-protein interactions, or kinase activity.";
        }
      },
      error: (err) => {
        console.error('Error calling PTM Site API:', err);
        this.mappingInProgress = false;
        this.isLoading = false;
        this.handleError('Failed to process question: ' + (err.message || 'Unknown error'));
      }
    });
  }
  
  // Fill template with extracted entities
  fillTemplateWithEntities(template: string, entities: any): string {
    let filledTemplate = template;
    
    // Check if we're working with the demo question or a real question
    const isUsingDemoQuestion = template === this.demoMappedQuestion;
    console.log('fillTemplateWithEntities - Using demo question?', isUsingDemoQuestion);
    console.log('fillTemplateWithEntities - Template:', template);
    console.log('fillTemplateWithEntities - Entities:', entities);
    
    // Handle special case for the CPK27/tonoplast question format
    if (template.includes("'CPK27'") && template.includes("'tonoplast sugar transporter 2'")) {
      console.log('Detected CPK27/tonoplast question format');
      
      // If user has modified the species, update it in the question
      if (entities.species && entities.species.trim() !== '') {
        // Replace the species in quotes
        filledTemplate = filledTemplate.replace(/'Arabidopsis thaliana'/, `'${entities.species}'`);
      }
      
      // If user has modified protein1 (CPK27), update it
      if (entities.proteins && entities.proteins.length >= 1 && entities.proteins[0].trim() !== '') {
        // Replace CPK27 in quotes
        filledTemplate = filledTemplate.replace(/'CPK27'/, `'${entities.proteins[0]}'`);
      }
      
      // If user has modified protein2 (tonoplast sugar transporter 2), update it
      if (entities.proteins && entities.proteins.length >= 2 && entities.proteins[1].trim() !== '') {
        // Replace tonoplast sugar transporter 2 in quotes
        filledTemplate = filledTemplate.replace(/'tonoplast sugar transporter 2'/, `'${entities.proteins[1]}'`);
      }
      
      console.log('Modified question after entity substitution:', filledTemplate);
      return filledTemplate;
    }
    
    // Standard template processing for other question formats
    // Replace species placeholder
    if (entities.species) {
      filledTemplate = filledTemplate.replace("'{Species}'", `'${entities.species}'`);
    }
    
    // Replace protein name placeholders based on the number of proteins extracted
    if (entities.proteins && entities.proteins.length > 0) {
      if (filledTemplate.includes("'{ProteinName}'")) {
        filledTemplate = filledTemplate.replace("'{ProteinName}'", `'${entities.proteins[0]}'`);
      }
      
      if (filledTemplate.includes("'{ProteinName1}'") && entities.proteins.length >= 1) {
        filledTemplate = filledTemplate.replace("'{ProteinName1}'", `'${entities.proteins[0]}'`);
      }
      
      if (filledTemplate.includes("'{ProteinName2}'") && entities.proteins.length >= 2) {
        filledTemplate = filledTemplate.replace("'{ProteinName2}'", `'${entities.proteins[1]}'`);
      } else if (filledTemplate.includes("'{ProteinName2}'")) {
        // If we need a second protein but don't have one, use a placeholder
        filledTemplate = filledTemplate.replace("'{ProteinName2}'", "the target protein");
      }
    }
    
    return filledTemplate;
  }

  // Get answer for the mapped and filled question using PTM Site API
  getAnswerForMappedQuestion(formattedQuestion: string): void {
    // Define the API URL for getting answers
    const apiUrl = 'https://ptmsite.info:5004/api/answer_question/';
    
    // Debug: Check if this is the demo question
    const isDemoQuestion = formattedQuestion === this.demoMappedQuestion;
    console.log('Is this the demo question?', isDemoQuestion);
    console.log('ACTUAL QUESTION BEING SENT:', formattedQuestion);
    
    // Debug: Log exact character comparison
    if (formattedQuestion.includes('CPK27') && formattedQuestion.includes('tonoplast')) {
      console.log('Question contains CPK27 and tonoplast');
      console.log('Character by character comparison with demo:');
      console.log('Length match?', formattedQuestion.length === this.demoMappedQuestion.length);
      
      // Find any character differences
      for (let i = 0; i < Math.min(formattedQuestion.length, this.demoMappedQuestion.length); i++) {
        if (formattedQuestion[i] !== this.demoMappedQuestion[i]) {
          console.log(`Difference at position ${i}: '${formattedQuestion[i]}' vs '${this.demoMappedQuestion[i]}'`);
        }
      }
    }
    
    // Ensure we're using the provided formatted question, not the demo question
    const questionToSend = formattedQuestion.trim();
    
    // Prepare the request body
    const requestBody = {
      question: questionToSend
    };
    
    // Set headers for JSON content
    const headers = { 'Content-Type': 'application/json' };
    
    console.log('Sending question to answer API:', questionToSend);
    console.log('Request body:', requestBody);
    
    // Make the API call to get the answer
    this.httpClient.post(apiUrl, requestBody, { headers }).subscribe({
      next: (result: any) => {
        this.isLoading = false;
        console.log('PTM Site API answer response:', result);
        
        // Handle the specific API response format
        // Expected format: { data: [{ answer: "Yes" }], execution_time: 0.48, message: "Successfully answered question", status: 1 }
        console.log('Processing API response:', result);
        
        // Debug: Store raw response for comparison
        const rawResponse = JSON.stringify(result);
        console.log('Raw API response:', rawResponse);
        
        if (result && result.data && Array.isArray(result.data) && result.data.length > 0) {
          // Extract answer from the data array
          const answerObj = result.data[0];
          console.log('Answer object from API:', answerObj);
          
          if (answerObj && answerObj.answer) {
            this.messageContent = answerObj.answer;
            console.log('Extracted answer:', this.messageContent);
            
            // Debug: Compare with demo answer
            const isDemoAnswer = this.messageContent === this.demoAnswer;
            console.log('Is this the demo answer?', isDemoAnswer);
            
            // Debug: Log the first few characters of both for comparison
            console.log('First 20 chars of response:', this.messageContent.substring(0, 20));
            console.log('First 20 chars of demo:', this.demoAnswer.substring(0, 20));
          } else {
            console.error('No answer property in data[0]:', answerObj);
            this.handleError('Failed to get answer: Missing answer property in response');
          }
        } else if (result && result.answer) {
          // Direct answer property
          if (typeof result.answer === 'object') {
            // If answer is an object, try to extract text
            if (result.answer.text) {
              this.messageContent = result.answer.text;
            } else {
              // Convert the object to a string
              try {
                this.messageContent = JSON.stringify(result.answer, null, 2);
              } catch (e) {
                this.messageContent = 'Answer received but could not be displayed properly.';
              }
            }
          } else {
            // If answer is a string or primitive
            this.messageContent = result.answer;
          }
        } else if (result && typeof result === 'string') {
          // If the API returns a string directly
          this.messageContent = result;
        } else if (result && result.message && result.status === 1) {
          // If we have a success message but no data
          this.messageContent = 'The system processed your question but did not return a specific answer.';
        } else {
          // Log the full response for debugging
          console.error('Unexpected API response format:', result);
          this.handleError('Failed to get answer: Unexpected response format. Check console for details.');
        }
      },
      error: (err) => {
        console.error('API error:', err);
        this.handleError('Error getting answer: ' + (err.message || 'Unknown error'));
      }
    });
  }
  
  // Reset the form
  resetForm(): void {
    this.userQuestion = '';
    this.mappedQuestion = '';
    this.messageContent = '';
    this.isLoading = false;
    this.mappingInProgress = false;
    this.userSpecies = '';
    this.userProtein1 = '';
    this.userProtein2 = '';
    this.extractedEntities = null;
  }
  
  // Test the API directly with a fixed question to compare with manual testing
  testApiDirectly(): void {
    // Use the exact same question format as your manual test
    const testQuestion = "In 'Arabidopsis thaliana', do the kinase 'CPK27' phosphorylate 'tonoplast sugar transporter 2'? Please answer yes or no.";
    
    // Define the API URL for getting answers
    const apiUrl = 'https://ptmsite.info:5004/api/answer_question/';
    
    // Prepare the request body exactly as you would in manual testing
    const requestBody = {
      question: testQuestion
    };
    
    // Set headers for JSON content
    const headers = { 'Content-Type': 'application/json' };
    
    console.log('TEST: Sending direct API test with question:', testQuestion);
    console.log('TEST: Request body:', JSON.stringify(requestBody));
    
    // Make the API call directly
    this.httpClient.post(apiUrl, requestBody, { headers }).subscribe({
      next: (result: any) => {
        console.log('TEST: Direct API test response:', result);
        
        if (result && result.data && Array.isArray(result.data) && result.data.length > 0) {
          const answerObj = result.data[0];
          if (answerObj && answerObj.answer) {
            console.log('TEST: Direct API test answer:', answerObj.answer);
          }
        }
      },
      error: (err) => {
        console.error('TEST: Direct API test error:', err);
      }
    });
  }

  // Generate link to UniversalSearch with GeneSymbol search type
  getGeneSymbolLink(geneValue: string): string {
    if (!geneValue || geneValue.trim() === '') {
      return '';
    }
    const baseUrl = window.location.origin;
    return `${baseUrl}/UniversalSearch?query=${encodeURIComponent(geneValue)}&searchType=GeneSymbol`;
  }

  // Update answer with user-modified entities
  updateWithUserEntities(): void {
    if (!this.mappedQuestion) {
      return;
    }
    
    // Create a modified entities object with user input
    const modifiedEntities = {
      species: this.userSpecies,
      proteins: [this.userProtein1, this.userProtein2].filter(p => p && p.trim() !== '')
    };
    
    console.log('User modified entities:', modifiedEntities);
    console.log('Original mapped question:', this.mappedQuestion);
    
    // Fill the template with user-modified entities
    const filledTemplate = this.fillTemplateWithEntities(this.mappedQuestion, modifiedEntities);
    console.log('Filled template after user modifications:', filledTemplate);
    
    // Debug: Compare with demo question
    console.log('Is filled template the same as demo question?', filledTemplate === this.demoMappedQuestion);
    
    // Get answer for the updated template
    this.isLoading = true;
    this.getAnswerForMappedQuestion(filledTemplate);
  }

  // Handle errors
  private handleError(message: string): void {
    console.error(message);
    this.messageContent = message;
    this.isLoading = false;
    this.mappingInProgress = false;
  }
}
